import { BigNumberish, uint256, UINT_256_MAX } from 'starknet';
import { Decimal as DecimalBase, Numberish } from '@tempusfinance/decimal';

export type Uint256 = { low: BigNumberish; high: BigNumberish };
export const DEFAULT_DECIMAL_PRECISION = DecimalBase.PRECISION;

const isUint256 = (value: Numberish | bigint | Uint256): value is Uint256 => {
  return (value as Uint256).low !== undefined && (value as Uint256).high !== undefined;
};

export default class Decimal extends DecimalBase {
  static MAX_DECIMAL = new Decimal(UINT_256_MAX, DEFAULT_DECIMAL_PRECISION);
  static ZERO = new Decimal(0);

  // In case user passes bigint as value, we also need user to provide precision
  constructor(value: bigint | Uint256, valuePrecision: number);
  // In case user passes (number | string | Decimal) as value, we don't need precision because
  // we can safely convert (number | string | Decimal) to bigint with 18 decimal precision.
  constructor(value: Numberish);

  // Constructor implementation
  constructor(value: Numberish | bigint | Uint256, valuePrecision: number = Decimal.PRECISION) {
    if (isUint256(value)) {
      super(uint256.uint256ToBN(value), valuePrecision);
    } else if (typeof value === 'bigint') {
      super(value, valuePrecision);
    } else {
      super(value);
    }
  }

  static parse(value: bigint, defaultValue: bigint, valuePrecision: number): Decimal;
  static parse(value: Numberish, defaultValue: Numberish): Decimal;
  static parse(
    value: Numberish | bigint,
    defaultValue: Numberish | bigint,
    valuePrecision = Decimal.PRECISION,
  ): Decimal {
    if (typeof value === 'bigint') {
      return new Decimal(DecimalBase.parse(value, defaultValue as bigint, valuePrecision));
    }

    return new Decimal(DecimalBase.parse(value, defaultValue as Numberish));
  }

  static max(a: Numberish, b: Numberish) {
    return new Decimal(DecimalBase.max(a, b));
  }

  static min(a: Numberish, b: Numberish) {
    return new Decimal(DecimalBase.min(a, b));
  }

  add(addend: Numberish): Decimal {
    return new Decimal(super.add(addend));
  }

  sub(subtrahend: Numberish): Decimal {
    return new Decimal(super.sub(subtrahend));
  }

  mul(multiplicand: Numberish): Decimal {
    return new Decimal(super.mul(multiplicand));
  }

  div(divisor: Numberish): Decimal {
    return new Decimal(super.div(divisor));
  }

  pow(exponent: number): Decimal {
    return new Decimal(super.pow(exponent));
  }

  abs(): Decimal {
    return new Decimal(super.abs());
  }

  toUint256(precision: number) {
    return { type: 'struct' as const, ...uint256.bnToUint256(this.toBigInt(precision)) };
  }
}

export const ZERO = new Decimal(DecimalBase.ZERO);
export const ONE = new Decimal(DecimalBase.ONE);
